import { useState } from 'react';
import { CaretDown, DownloadSimple, FileCsv, FilePdf, FileText, MicrosoftExcelLogo, SpinnerGap, type Icon } from '@phosphor-icons/react';
import { ImmoButton, Popover, SegmentedControl, tonFarben, type Ton } from '../../../../components/immobilien/ui';
import type { ExportColumn, ExportStats } from '../../../../lib/export/types';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { exportPdfReport, exportPdfTable } from '../../../../lib/export/exportPdf';

type Format = 'csv' | 'xlsx' | 'pdf-table' | 'pdf-report';
const FORMATE: readonly { key: Format; titel: string; text: string; icon: Icon; ton: Ton }[] = [
  { key: 'csv', titel: 'CSV', text: 'Kompatibel mit Excel', icon: FileCsv, ton: 'ok' },
  { key: 'xlsx', titel: 'Excel', text: 'Formatierte XLSX-Datei', icon: MicrosoftExcelLogo, ton: 'info' },
  { key: 'pdf-table', titel: 'PDF (Tabelle)', text: 'Schneller Tabellenexport', icon: FilePdf, ton: 'bad' },
  { key: 'pdf-report', titel: 'PDF (Bericht)', text: 'Formatiert mit Statistiken', icon: FileText, ton: 'acc' },
];

/** Export wie ExportDropdown (Umfang Ansicht/Alle Daten, vier Formate) im neuen Stil, mit Toast. */
export function ExportMenue<T>({ titel, spalten, ansicht, alle, statistik, onToast }: {
  titel: string;
  spalten: ExportColumn[];
  ansicht: readonly T[];
  alle: () => Promise<readonly T[]>;
  statistik: ExportStats;
  onToast: (text: string, fehler?: boolean) => void;
}) {
  const [umfang, setUmfang] = useState<'ansicht' | 'alle'>('ansicht');
  const [laeuft, setLaeuft] = useState(false);

  const exportiere = async (f: (typeof FORMATE)[number], schliessen: () => void) => {
    setLaeuft(true);
    try {
      const daten = umfang === 'alle' ? await alle() : ansicht;
      const config = { title: titel, columns: spalten, data: [...daten], stats: statistik };
      if (f.key === 'csv') exportCsv(config);
      else if (f.key === 'xlsx') exportXlsx(config);
      else if (f.key === 'pdf-table') exportPdfTable(config);
      else exportPdfReport(config);
      schliessen();
      onToast(`Export „${titel}“ als ${f.titel} erstellt (${umfang === 'alle' ? 'alle Daten' : `${daten.length} Zeilen`})`);
    } catch (e: unknown) {
      onToast(`Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Popover label="Export" breite={260} ausloeser={({ offen, umschalten, ref }) => (
      <ImmoButton ref={ref} groesse={44} pille={false} icon={laeuft ? SpinnerGap : DownloadSimple} onClick={umschalten} disabled={laeuft}
        aria-expanded={offen} aria-haspopup="menu" style={{ fontSize: 13, padding: '0 16px' }}>
        Export<CaretDown size={11} weight="bold" color="var(--ink3)" />
      </ImmoButton>
    )}>
      {schliessen => (
        <div className="flex flex-col" style={{ gap: 8, padding: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', padding: '2px 4px 0' }}>Umfang</span>
          <SegmentedControl voll hoehe={30} ariaLabel="Umfang" wert={umfang} onChange={setUmfang}
            segmente={[{ key: 'ansicht', label: `Ansicht (${ansicht.length})` }, { key: 'alle', label: 'Alle Daten' }]} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)', padding: '4px 4px 0' }}>Format</span>
          {FORMATE.map(f => {
            const { bg, fg } = tonFarben(f.ton);
            const IconKomp = f.icon;
            return (
              <button key={f.key} type="button" role="menuitem" disabled={laeuft} onClick={() => { void exportiere(f, schliessen); }}
                className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
                style={{ gap: 12, padding: '8px', borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: 'inherit', opacity: laeuft ? 0.5 : 1 }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 11, background: bg, color: fg }}>
                  <IconKomp weight="fill" size={17} />
                </span>
                <span className="flex flex-col min-w-0">
                  <span style={{ fontSize: 13, fontWeight: 800 }}>{f.titel}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{f.text}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Popover>
  );
}
